import { InjectionToken } from '@angular/core';
import {
  ComparableGroup,
  EngineSettings,
  IndexDef,
  IndexHistoryPoint,
  Part,
  PurchaseEvent,
  ClauseRegisterEntry,
} from '../craig-engine-data';
import type {
  SupplierContract,
  BidSku,
  Assumption,
  Decision,
} from '../mock-data';
import type { UserStory } from '../user-stories-ledger';

/**
 * Identity facts shown in footer / header of the contract-clause view.
 * `customer` is brand-specific (e.g. MEPPI corporate identity); `spendkey`
 * is always Spendkey itself.
 */
export interface ContractClauseIdentity {
  customer: {
    fullName: string;
    hq?: string;
    parent?: string;
    sourceUrl: string;
  };
  spendkey: {
    domain: string;
    sourceUrl: string;
  };
}

/**
 * One row in the §24.4.5 / indexed-basket weight table.
 */
export interface BasketWeightRow {
  indexCode: string;
  weight: number;
  clauseLabel: string;
}

/**
 * PricingIndexProfile — the per-deployment switchboard for the
 * Pricing Index Dashboard. Holds every seed value and every
 * customer-specific copy string the component needs, so a brand-neutral
 * "demo" variant and the MEPPI customer variant can share the same
 * component tree.
 *
 * Two profiles ship today:
 *   - MEPPI_PROFILE  → existing /pricing-index-dashboard route (verbatim)
 *   - DEMO_PROFILE   → /pricing-index-dashboard-demo route (brand neutral)
 *
 * Tests that don't override the token receive MEPPI_PROFILE via the
 * default factory, so historical behaviour is preserved without changes.
 */
export interface PricingIndexProfileStrings {
  /** Header sub-title under the page title. */
  pageSubtitle: string;
  /** Friendly bid name shown across summary cards. */
  bidName: string;
  /** Label for the primary escalation clause, e.g. '§24.4 escalation' or 'Standard escalation clause'. */
  escalationClauseLabel: string;
  /** Label for the indexed-basket reconciler clause, e.g. '§24.4.5 basket' or 'Indexed basket reconciler'. */
  basketReconcilerLabel: string;
  /** Label for the partial pass-through clause, e.g. '§24.4 partial' or 'Partial pass-through clause'. */
  partialClauseLabel: string;
  /** Generic clause group name, e.g. '§24.4' or 'Standard escalation clause'. */
  escalationMechanismName: string;
  /** Name of the source contract — e.g. 'Authority Contract' or 'Customer contract'. */
  contractName: string;
  /** Source workbook / attachment reference label, e.g. 'Attachment B v1' or 'Customer pricing schedule v1'. */
  attachmentReference: string;
  /** Customer identity display name shown in the footer / hero. */
  customerDisplayName: string;
  /** Customer "about" URL (footer link). May be empty for the generic profile. */
  customerSourceUrl: string;
  /** Footer factual statement about who the dashboard was prepared for. */
  customerFactualStatement: string;
  /** File-name prefix for Excel template downloads. */
  excelTemplatePrefix: string;
  /** Reset-confirmation copy referencing the seed dataset name. */
  resetSeedDescription: string;
  /** Full descriptor used in the Excel-upload replace-confirmation dialog,
   * e.g. 'MEPPI demo dataset' or 'illustrative demo dataset'. */
  resetSeedDatasetDescriptor: string;
  /** Clause ID used by the Parts Master keyword-suggestion engine for
   * full-pass-through keywords (panel, frame, beam, connector, wiring,
   * loom). MEPPI: '§24.4'; demo: 'Standard escalation clause'. */
  passthroughFullClauseId: string;
  /** Clause ID used by the Parts Master keyword-suggestion engine for
   * partial-pass-through keywords (motor, rotor, compressor, hvac).
   * MEPPI: '§24.4 partial'; demo: 'Partial pass-through clause'. */
  passthroughPartialClauseId: string;
  /** Chart-series label for the indexed basket "actual" line on the
   * legacy contract-clause view. MEPPI: '§24.4.5 basket — actual
   * (% since base)'; demo: 'Indexed basket — actual (% since base)'. */
  basketReconcilerActualLabel: string;
  /** matTooltip on the Parts Master "Recovery" column header. MEPPI
   * keeps the historic '§24.4 escalation' example; demo uses a neutral
   * 'standard escalation clause' example. */
  partsMasterRecoveryColumnTooltip: string;

  // ─── Contract-clause-view specific strings ────────────────────────────
  /** Sub-section name shown on the orientation strip + tab bar sub-text.
   * MEPPI: '§24.4.5 basket'; demo: 'Indexed basket'. */
  contractClauseTabSub: string;
  /** Tab-bar primary sub-text — slightly longer flavour text under the
   * "Contract clause view" tab. MEPPI: '§24.4.5 basket reconciler';
   * demo: 'Indexed basket reconciler'. */
  contractClauseTabBarSub: string;
  /** Orientation-strip tooltip on the contract-clause flow step. */
  contractClauseOrientationTooltip: string;
  /** Tab-bar tooltip on the contract-clause tab button. */
  contractClauseTabTooltip: string;
  /** Header subtitle prefix inside the contract-clause tab.
   * MEPPI: '§24.4.5 basket reconciler'; demo: 'Indexed basket reconciler'. */
  contractClauseHeaderSubtitlePrefix: string;
  /** "Prototype/illustrative workspace" trailing label after the customer
   * name link in the header subtitle. */
  contractClauseHeaderSubtitleSuffix: string;
  /** matTooltip on the "Base price" edit hint inside the summary strip. */
  contractClauseBasePriceTooltip: string;
  /** matTooltip on the "Next contractual review" edit hint. */
  contractClauseNextReviewTooltip: string;
  /** matTooltip on the "Pending claim" edit hint. */
  contractClausePendingClaimTooltip: string;
  /** Hero chart panel title (the basket movement chart). */
  contractClauseHeroTitle: string;
  /** matTooltip on the hero chart info icon. */
  contractClauseHeroTooltip: string;
  /** Header for the per-index basket cards section. */
  contractClauseBasketSectionTitle: string;
  /** matTooltip on the per-index weight input. */
  contractClauseWeightInputTooltip: string;
  /** Negotiation reconciler intro / explanation text. */
  contractClauseReconcilerIntro: string;
  /** matTooltip on the reconciler info icon. */
  contractClauseReconcilerTooltip: string;
  /** matTooltip on the math breakdown "Index" column header. */
  contractClauseMathIndexTooltip: string;
  /** matTooltip on the math breakdown "Weight" column header. */
  contractClauseMathWeightTooltip: string;
  /** Escalation invoicing calculator: row label for movement since base. */
  contractClauseMovementSinceBaseLabel: string;
  /** matTooltip on the pass-through info icon. */
  contractClausePassThroughTooltip: string;
  /** matTooltip on the bid-skus "Matched index" header. */
  contractClauseBidSkusMatchedIndexTooltip: string;
  /** matTooltip on the bid-skus "Coverage" header. */
  contractClauseBidSkusCoverageTooltip: string;
  /** Empty-state note when no per-supplier indices are loaded yet. */
  contractClauseEmptyStateNote: string;
  /** User-guide list item describing the contract-clause view. */
  contractClauseUserGuideText: string;
  /** Footer disclaimer that runs when showCustomerIdentityFooter is true. */
  contractClauseFooterDisclaimer: string;
  /** Footer "Sources" link label for the customer identity (e.g.
   * 'MEPPI (corporate identity)'). */
  contractClauseCustomerSourceLinkLabel: string;
  /** Unit label appended to the basket-vs-base KPI $ figures inside the
   * hero forecast callouts ("≈ $X per <label>"). MEPPI: 'per car set';
   * demo: 'per unit set' (precision-machining neutral). */
  basketKpiUnitLabel: string;
}

export interface PricingIndexProfile {
  /** Stable profile identifier. Used to detect profile switches. */
  profileId: 'meppi' | 'demo';
  /** Long display name surfaced in tooltips / footers. */
  displayName: string;

  // ─── Seed data (drives the EngineStore default state) ──────────────────
  settings: EngineSettings;
  indices: IndexDef[];
  comparableGroups: ComparableGroup[];
  parts: Part[];
  purchases: PurchaseEvent[];
  indexHistory: IndexHistoryPoint[];
  clauseRegister: ClauseRegisterEntry[];

  // ─── Contract-clause view data (only rendered if showContractClauseView=true) ─
  /** Supplier contracts shown in the contract-clause-view supplier picker. */
  contractClauseSuppliers: SupplierContract[];
  /** Bid SKU rows shown in the contract-clause-view bid buffer table. */
  contractClauseBidSkus: BidSku[];
  /** "Assumptions" panel rows. */
  contractClauseAssumptions: Assumption[];
  /** "Decisions" panel rows. */
  contractClauseDecisions: Decision[];
  /** Programme-level basket weights shown in the math breakdown. */
  contractClauseBasket: BasketWeightRow[];
  /** Identity facts shown in the header subtitle and footer sources. */
  contractClauseIdentity: ContractClauseIdentity;

  // ─── Brand strings & copy ──────────────────────────────────────────────
  strings: PricingIndexProfileStrings;

  // ─── User Stories ledger payload ───────────────────────────────────────
  /** Pre-built ledger array. MEPPI uses the verbatim spec IDs; demo uses
   *  anonymized F-NN labels with traceability stripped. */
  userStoriesLedger: UserStory[];
  /** Whether the per-story build-status pill (built / partial / not-yet)
   *  is rendered inside the slide-over. Hidden on demo so marketing copy
   *  doesn't expose internal delivery state. */
  userStoriesLedgerShowStatusBadges: boolean;

  // ─── Feature flags ─────────────────────────────────────────────────────
  /** Whether the User Stories ledger button + slide-over is rendered. */
  showUserStoriesLedger: boolean;
  /**
   * Whether the legacy §24.4.5 contract-clause-view tab is rendered.
   * The contract-clause view relies heavily on the MEPPI Attachment B v1
   * basket and identity facts; for brand-neutral demos we hide it and
   * route prospects to the bid-buffer / supplier / recovery / admin tabs
   * which are fully profile-driven.
   */
  showContractClauseView: boolean;
  /** Whether the MEPPI-branded identity footer is rendered. */
  showCustomerIdentityFooter: boolean;
}

// Import is value-level but the cycle is safe: meppi-profile only imports
// the *types* from this file, not the token itself.
import { MEPPI_PROFILE } from './meppi-profile';

/**
 * Default-factory returns MEPPI_PROFILE, so consumers that don't provide
 * the token (every existing spec + the original route) keep their
 * historical behaviour.
 */
export const PRICING_INDEX_PROFILE = new InjectionToken<PricingIndexProfile>(
  'PRICING_INDEX_PROFILE',
  {
    providedIn: 'root',
    factory: () => MEPPI_PROFILE,
  },
);
